import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Avatar,
  Stack,
  LinearProgress,
  Tooltip
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../hooks/useLocalization';

const MyBalance = () => {
  const { t } = useLocalization();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openSetDialog, setOpenSetDialog] = useState(false);
  const [formData, setFormData] = useState({
    tokens_to_add: '',
    tokens: ''
  });
  const { user } = useAuth();

  const fetchBalance = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/client-balance/balance');
      setBalance(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || t('failed_to_fetch_balance'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, []);

  const handleOpenAddDialog = () => {
    setFormData({ tokens_to_add: '', tokens: '' });
    setOpenAddDialog(true);
  };

  const handleOpenSetDialog = () => {
    setFormData({ tokens_to_add: '', tokens: balance?.tokens?.toString() || '0' });
    setOpenSetDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenAddDialog(false);
    setOpenSetDialog(false);
    setFormData({ tokens_to_add: '', tokens: '' });
  };

  const handleAddTokens = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/client-balance/add-tokens', {
        tokens_to_add: parseInt(formData.tokens_to_add)
      });
      handleCloseDialogs();
      fetchBalance();
    } catch (err) {
      setError(err.response?.data?.detail || t('failed_to_add_tokens'));
    }
  };

  const handleSetTokens = async (e) => {
    e.preventDefault();
    try {
      await axios.put('/client-balance/set-tokens', {
        tokens: parseInt(formData.tokens)
      });
      handleCloseDialogs();
      fetchBalance();
    } catch (err) {
      setError(err.response?.data?.detail || t('failed_to_set_tokens'));
    }
  };

  const getTokenColor = (tokens) => {
    if (tokens > 100) return 'success';
    if (tokens > 50) return 'warning';
    return 'error';
  };

  const getTokenStatus = (tokens) => {
    if (tokens > 100) return t('status_excellent');
    if (tokens > 50) return t('status_good');
    if (tokens > 10) return t('status_low');
    return t('status_critical');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          <AccountBalanceWalletIcon sx={{ mr: 2, fontSize: 'inherit' }} />
          {t('token_balance')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
          {t('manage_integration_tokens_usage')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {balance && (
        <>
          {/* Main Balance Card */}
          <Card sx={{ 
            mb: 4, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              zIndex: 0
            }
          }}>
            <CardContent sx={{ position: 'relative', zIndex: 1, p: 4 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      border: '3px solid rgba(255, 255, 255, 0.3)'
                    }}
                  >
                    <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />
                  </Avatar>
                </Grid>
                <Grid item xs>
                  <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {balance.tokens} {t('tokens_text')}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                    {t('client_text')}: {balance.name} (ID: {balance.client_id})
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {t('created_text')}: {new Date(balance.created_at).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item>
                  <Stack spacing={2} alignItems="center">
                    <Chip
                      icon={<AccountBalanceWalletIcon />}
                      label={`${balance.tokens} ${t('tokens_text').toLowerCase()}`}
                      color="default"
                      variant="filled"
                      size="large"
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1.1rem'
                      }}
                    />
                    <Chip
                      label={getTokenStatus(balance.tokens)}
                      color={getTokenColor(balance.tokens)}
                      variant="filled"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{ 
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {t('add_tokens')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleOpenSetDialog}
              sx={{ 
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {t('set_balance')}
            </Button>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                color: 'white',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                }
              }}>
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Avatar sx={{ 
                    width: 50, 
                    height: 50, 
                    mx: 'auto', 
                    mb: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    border: '2px solid rgba(255, 255, 255, 0.3)'
                  }}>
                    <AttachMoneyIcon />
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {balance.tokens}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    {t('current_balance')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                color: 'text.primary',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                }
              }}>
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Avatar sx={{ 
                    width: 50, 
                    height: 50, 
                    mx: 'auto', 
                    mb: 2,
                    bgcolor: 'primary.main'
                  }}>
                    <TrendingUpIcon />
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                    {getTokenStatus(balance.tokens)}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    {t('status')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                }
              }}>
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Avatar sx={{ 
                    width: 50, 
                    height: 50, 
                    mx: 'auto', 
                    mb: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    border: '2px solid rgba(255, 255, 255, 0.3)'
                  }}>
                    <HistoryIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {new Date().toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {t('last_updated')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                color: 'white',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                }
              }}>
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Avatar sx={{ 
                    width: 50, 
                    height: 50, 
                    mx: 'auto', 
                    mb: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    border: '2px solid rgba(255, 255, 255, 0.3)'
                  }}>
                    <AccountBalanceWalletIcon />
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {balance.tokens > 0 ? t('active') : t('inactive')}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    {t('account_status')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Add Tokens Dialog */}
      <Dialog 
        open={openAddDialog} 
        onClose={handleCloseDialogs}
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {t('add_tokens_to_balance')}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t('tokens_to_add')}
            type="number"
            fullWidth
            value={formData.tokens_to_add}
            onChange={(e) => setFormData({ ...formData, tokens_to_add: e.target.value })}
            helperText={t('current_balance_tokens').replace('{tokens}', balance?.tokens || 0)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCloseDialogs}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleAddTokens} 
            variant="contained" 
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #3dd870 0%, #2ee8c7 100%)',
              }
            }}
          >
            {t('add_tokens')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Set Tokens Dialog */}
      <Dialog 
        open={openSetDialog} 
        onClose={handleCloseDialogs}
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {t('set_token_balance')}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t('new_token_balance')}
            type="number"
            fullWidth
            value={formData.tokens}
            onChange={(e) => setFormData({ ...formData, tokens: e.target.value })}
            helperText={t('current_balance_tokens').replace('{tokens}', balance?.tokens || 0)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCloseDialogs}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleSetTokens} 
            variant="contained" 
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            {t('set_balance')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyBalance; 